/*
 * Dual commerce events: purchase, product_purchase, product_purchase_quantity,
 * payment_failed, register_success, funnel_events. Written through
 * trackOptimizelyEvent (WebX data layer + FX mirror).
 */
import {getFxTrackingContext} from './optimizely-fx-events'
import {trackOptimizelyEvent} from './optimizely-event'
import {categoriesFromList, getExpPageContext} from './optimizely-exp-page-context'
import {getVisitorAttributes} from './optimizely-visitor-context'

const toNumber = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
}

const currentUrl = () => (typeof window !== 'undefined' ? window.location.href : undefined)

/** `visa` / `applepay` / `paypal`… from the Adyen payment method + card brand. */
export const paymentMethodLabel = (paymentMethod, cardBrand) => {
    const type = String(paymentMethod?.type || '').toLowerCase()
    if (type === 'scheme') return String(cardBrand || paymentMethod?.brand || 'card').toLowerCase()
    return type || undefined
}

/** `standard` / `express` / `bopis` / `same_day` / `egiftcard` from the SCAPI shipping method. */
export const shippingMethodLabel = (method) => {
    if (!method) return undefined
    if (method.c_storePickupEnabled) return 'bopis'
    if (method.c_SameDayShipMethod) return 'same_day'
    const id = String(method.id || method.name || '').toLowerCase()
    if (id.includes('gift')) return 'egiftcard'
    return String(method.c_signifydShippingMethodType || id || 'standard').toLowerCase()
}

/**
 * Normalise SCAPI order/basket line items into the purchase-event shape.
 * @param {Array<Object>} productItems
 * @param {string} [orderNo]
 */
export const buildPurchasedItems = (productItems, orderNo) =>
    (Array.isArray(productItems) ? productItems : []).map((item) => {
        const quantity = toNumber(item.quantity) || 1
        const listPrice = toNumber(item.price)
        const lineTotal =
            toNumber(item.priceAfterOrderDiscount) ??
            toNumber(item.priceAfterItemDiscount) ??
            listPrice ??
            0
        const categories = categoriesFromList(item.c_categories)
        return {
            productId: item.productId,
            name: item.productName || item.itemText,
            category: categories.Category || item.c_primaryCategory,
            subcategory: categories.Subcategory,
            quantity,
            unitPrice:
                toNumber(item.basePrice) ?? (listPrice != null ? listPrice / quantity : undefined),
            lineTotal,
            orderNo,
            variationGroup: item.c_variationGroup,
            addLocation: item.c_itemAddLocation,
            size: item.c_size,
            color: item.c_colorName,
            discounted:
                (listPrice != null && lineTotal < listPrice) || !!item.priceAdjustments?.length
        }
    })

/**
 * Track a checkout funnel transition in Web Experimentation and Feature
 * Experimentation with the same event key and properties.
 *
 * @param {Object} params
 * @param {string} params.id - Funnel step or action.
 * @param {string} [params.state] - Step state (e.g. "viewed", "completed").
 * @param {string} [params.result] - Outcome or destination.
 * @param {string} [params.URL] - URL override (used when abandoning checkout).
 */
export const trackFunnelEvent = ({id, state, result, URL: url}) => {
    const context = getExpPageContext()
    trackOptimizelyEvent({
        eventName: 'funnel_events',
        properties: {
            Category: context.Category,
            Subcategory: context.Subcategory,
            URL: url || context.URL,
            SKU: context.SKU,
            id,
            state,
            result
        }
    })
}

/**
 * The single revenue carrier: once per authorised order.
 * @param {{order: Object, basket?: Object, amountMinor?: number, paymentMethod?: Object, cardBrand?: string, threeDs?: boolean}} params
 */
export const trackPurchase = ({order, basket, amountMinor, paymentMethod, cardBrand, threeDs}) => {
    const productItems = order?.productItems || basket?.productItems || []
    const orderTotal =
        toNumber(order?.orderTotal) ??
        toNumber(basket?.orderTotal) ??
        (amountMinor != null ? toNumber(amountMinor) / 100 : undefined)
    if (orderTotal == null) return
    const shippingTotal = toNumber(order?.shippingTotal) ?? toNumber(basket?.shippingTotal)
    const promoApplied =
        !!order?.orderPriceAdjustments?.length ||
        !!order?.couponItems?.length ||
        productItems.some((item) => item.priceAdjustments?.length)
    const addLocations = [
        ...new Set(productItems.map((item) => item.c_itemAddLocation).filter(Boolean))
    ]

    trackOptimizelyEvent({
        eventName: 'purchase',
        tags: {revenue: Math.round(orderTotal * 100), value: orderTotal},
        properties: {
            URL: currentUrl(),
            Text: order?.orderNo,
            order_total: orderTotal,
            items_count: productItems.reduce(
                (sum, item) => sum + (toNumber(item.quantity) || 0),
                0
            ),
            customer_type: getFxTrackingContext().attributes?.is_logged_in ? 'registered' : 'guest',
            device_type: getVisitorAttributes().device,
            payment_method: paymentMethodLabel(paymentMethod, cardBrand),
            shipping_method: shippingMethodLabel(order?.shipments?.[0]?.shippingMethod),
            free_shipping: shippingTotal === 0,
            promo_applied: promoApplied,
            three_ds: !!threeDs,
            add_locations: addLocations.join('|') || undefined
        }
    })
}

/** Units sold per line — the `value` twin of product_purchase. */
export const trackProductPurchaseQuantity = (item) => {
    trackOptimizelyEvent({
        eventName: 'product_purchase_quantity',
        tags: {value: item.quantity},
        properties: {
            SKU: item.productId,
            URL: currentUrl(),
            Text: item.name,
            quantity: item.quantity,
            order_no: item.orderNo,
            variation_group: item.variationGroup,
            add_location: item.addLocation,
            size: item.size,
            color: item.color
        }
    })
}

/**
 * Track a "product_purchase" event per purchased line (value = line total in
 * dollars) and its twin "product_purchase_quantity" (value = units). Both are
 * dual-written. Order revenue travels on the "purchase" event, not here.
 *
 * @param {Array<Object>} items from buildPurchasedItems
 */
export const trackProductPurchasesEvent = (items = []) => {
    if (!Array.isArray(items)) return

    items.forEach((item) => {
        trackOptimizelyEvent({
            eventName: 'product_purchase',
            tags: {
                value: item.lineTotal
            },
            properties: {
                SKU: item.productId,
                Category: item.category,
                Subcategory: item.subcategory,
                Text: item.name,
                quantity: item.quantity,
                unit_price: item.unitPrice,
                line_total: item.lineTotal,
                order_no: item.orderNo,
                variation_group: item.variationGroup,
                add_location: item.addLocation,
                size: item.size,
                color: item.color,
                discounted: item.discounted
            }
        })
        trackProductPurchaseQuantity(item)
    })
}

/**
 * @param {{orderNo?: string, resultCode?: string, refusalReason?: string, paymentMethod?: Object, cardBrand?: string, orderTotal?: number, threeDs?: boolean}} params
 */
export const trackPaymentFailed = ({
    orderNo,
    resultCode,
    refusalReason,
    paymentMethod,
    cardBrand,
    orderTotal,
    threeDs
}) => {
    trackOptimizelyEvent({
        eventName: 'payment_failed',
        tags: null,
        properties: {
            URL: currentUrl(),
            Text: orderNo,
            payment_method: paymentMethodLabel(paymentMethod, cardBrand),
            result_code: resultCode || 'Error',
            refusal_reason: refusalReason,
            order_total: toNumber(orderTotal),
            three_ds: !!threeDs
        }
    })
}

/**
 * Account created. Flags only — never the birthday or phone value itself.
 * @param {{birthday?: *, phone?: *, emailSubscribe?: *}} form
 */
export const trackRegisterSuccess = ({birthday, phone, emailSubscribe} = {}) => {
    trackOptimizelyEvent({
        eventName: 'register_success',
        tags: null,
        properties: {
            URL: currentUrl(),
            birthday: !!birthday,
            phone: !!phone,
            email_subscribe: !!emailSubscribe
        }
    })
}
